/** biome-ignore-all lint/complexity/noExcessiveCognitiveComplexity: TODO */
import { useGetSessionToken } from '@homestake/shared/hooks';
import { batch } from '@legendapp/state';
import { useMutation } from '@tanstack/react-query';
import Decimal from 'decimal.js';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { postApiGameFastBetMutation } from '../api/__generated__/@tanstack/react-query.gen';
import { AutoBetStopReason } from '../constants';
import {
  AutoBetOnWinLossAction,
  autoBetState$,
  cashoutAnimationState$,
  configurationState$,
  gameState$,
  ratAnimationState$,
  towersState$,
  winDisplayState$,
} from '../state';
import { freeBetState$ } from '../state/free-bet.state';
import { sessionState$ } from '../state/session.state';
import { walletState$ } from '../state/wallet.state';
import {
  showErrorNotification,
  showInfoNotification,
  showNotification,
  showSuccessNotification,
  showWarningNotification,
} from '../utils';
import {
  applyBetCaps,
  calculateDisplayAmounts,
  calculateNextBetAmount,
  shouldStopAutoBetting as checkShouldStop,
  formatProfitMessage,
  shouldShowCapNotification,
  validatePickedBricks,
} from '../utils/autobet.utils';
import { useBackendErrorNotification } from './useBackendErrorNotification.hook';
import { useCashoutSound } from './useCashoutSound.hook';
import { useClickSound } from './useClickSound.hook';
import { useLossSound } from './useLossSound.hook';

// How many bets to wait between showing cap notifications
const NOTIFICATION_FREQUENCY = 50;

// Debounce delay for start/stop operations
const DEBOUNCE_DELAY = 300;

// Maximum number of consecutive bet failures before stopping
const MAX_CONSECUTIVE_FAILURES = 3;

export const useAutoBet = () => {
  const { t } = useTranslation();
  const { token } = useGetSessionToken();
  const { showBackendErrorNotification } = useBackendErrorNotification();
  const autoBetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sound effects
  const { playSound: playCashoutSound } = useCashoutSound();
  const { playSound: playLossSound } = useLossSound();
  const { playSound: playClickSound } = useClickSound();

  // Notification counters for min/max cap
  const minCapCounter = useRef(0);
  const maxCapCounter = useRef(0);

  // Track consecutive failures
  const consecutiveFailures = useRef(0);

  const mutation = useMutation({
    ...postApiGameFastBetMutation(),
    onSuccess: ({ balance, towersResults, win, baseBonusResponseDto }) => {
      // Reset failure counter on success
      consecutiveFailures.current = 0;

      const betAmount = autoBetState$.betAmount.peek();
      const coefficient = configurationState$.coeficient.peek();

      // Calculate display amounts using utility function
      const { currentProfit, isWin, isLoss } = calculateDisplayAmounts(
        win,
        betAmount,
        coefficient
      );

      // Play sound effects based on result
      if (isWin) {
        ratAnimationState$.triggerAnimation('win', 1000);
        cashoutAnimationState$.triggerAnimation('cashout', 1000);

        // Show win display for auto bet - each round is separate
        const rowsCleared = autoBetState$.pickedBricks.peek().length;

        // Calculate actual payout from win amount (this is the total payout for this round)
        const totalPayout = win ? Number(win) / coefficient : 0;

        // Only show win display if there's an actual positive payout
        if (totalPayout > 0) {
          winDisplayState$.showWinDisplay(
            {
              betAmount,
              winAmount: win ?? 0,
              rowsCleared,
            },
            1000
          ); // Show for 1 second in autobet (shorter than manual)
        }

        playCashoutSound();
      } else if (isLoss) {
        ratAnimationState$.triggerAnimation('bad', 1000);
        playLossSound();
      }

      batch(() => {
        freeBetState$.update(baseBonusResponseDto, true);

        if (
          baseBonusResponseDto?.bonusCount === 0 &&
          baseBonusResponseDto.isActive === true
        ) {
          stopAutoBetting();
        }

        if (balance !== undefined && balance !== null) {
          walletState$.setBalance(balance);
        }

        if (towersResults) {
          towersState$.setAllBricks(towersResults ?? []);
        }

        // Update autobet statistics
        const newBetCount = autoBetState$.runningCurrentBetCount.peek() + 1;
        const newTotalProfit = Number(
          new Decimal(autoBetState$.runningTotalProfit.peek())
            .plus(currentProfit)
            .toFixed(2)
        );

        autoBetState$.runningCurrentBetCount.set(newBetCount);
        autoBetState$.runningTotalProfit.set(newTotalProfit);

        // Handle win/loss actions for bet amount adjustment
        if (isWin) {
          handleWinAction();
        } else if (isLoss) {
          handleLossAction();
        }
      });

      // Set betting to false after processing
      autoBetState$.setIsBetting(false);

      // Check if we should continue autobetting
      const isStillAutoBetting = autoBetState$.isAutoBetting.peek();

      if (isStillAutoBetting) {
        if (shouldStopAutoBetting()) {
          stopAutoBetting();
        } else {
          // Clear any existing timeout before setting new one
          if (autoBetTimeoutRef.current) {
            clearTimeout(autoBetTimeoutRef.current);
          }
          // Continue autobetting after a short delay
          autoBetTimeoutRef.current = setTimeout(() => {
            // Double-check we're still auto-betting (prevents race conditions)
            const stillAutoBetting = autoBetState$.isAutoBetting.peek();
            if (stillAutoBetting) {
              executeNextBet();
            }
          }, 1000); // 1 second delay between bets
        }
      }
    },
    onError: (error) => {
      showBackendErrorNotification(error);

      // Increment failure counter
      consecutiveFailures.current += 1;

      // Check if we've hit max failures
      if (consecutiveFailures.current >= MAX_CONSECUTIVE_FAILURES) {
        showErrorNotification({
          title: t('notifications.autoBetStoppedTitle'),
          message: t('notifications.autoBetStoppedMessage', {
            count: MAX_CONSECUTIVE_FAILURES,
          }),
          dataTestId: 'autobet-stopped-max-failures',
        });
        autoBetState$.setIsBetting(false);
        stopAutoBetting();
      } else {
        showWarningNotification({
          title: t('notifications.betFailedTitle'),
          message: t('notifications.betFailedMessage', {
            current: consecutiveFailures.current,
            max: MAX_CONSECUTIVE_FAILURES,
          }),
          dataTestId: 'bet-failed-attempt',
        });

        autoBetState$.setIsBetting(false);

        // Retry after a longer delay
        const isStillAutoBetting = autoBetState$.isAutoBetting.peek();

        if (isStillAutoBetting) {
          // Clear any existing timeout before setting new one
          if (autoBetTimeoutRef.current) {
            clearTimeout(autoBetTimeoutRef.current);
          }
          autoBetTimeoutRef.current = setTimeout(() => {
            const stillAutoBetting = autoBetState$.isAutoBetting.peek();
            if (stillAutoBetting) {
              executeNextBet();
            }
          }, 2000); // 2 second delay after error
        }
      }
    },
  });

  // Apply min/max caps to bet amount
  const applyCaps = useCallback((amount: number) => {
    const minBet = configurationState$.minBet.peek();
    const maxBet = configurationState$.maxBet.peek();
    const decimalScale = configurationState$.decimalScale.peek();
    const result = applyBetCaps(amount, minBet, maxBet, decimalScale);

    return result;
  }, []);

  // Handle cap notifications with frequency limiting
  const handleCapNotification = useCallback(
    (originalAmount: number) => {
      const minBet = configurationState$.minBet.peek();
      const maxBet = configurationState$.maxBet.peek();
      const _decimalScale = configurationState$.decimalScale.peek();
      if (originalAmount < minBet) {
        minCapCounter.current += 1;

        // Use utility function to check if should show notification
        if (
          shouldShowCapNotification(
            minCapCounter.current,
            NOTIFICATION_FREQUENCY
          )
        ) {
          showInfoNotification({
            title: t('notifications.autoBetInfoTitle'),
            message: t('notifications.minBetMessage', { minBet }),
            autoClose: 2000,
            dataTestId: 'autobet-min-cap-reached',
          });
        }
      } else if (originalAmount > maxBet) {
        maxCapCounter.current += 1;

        // Use utility function to check if should show notification
        if (
          shouldShowCapNotification(
            maxCapCounter.current,
            NOTIFICATION_FREQUENCY
          )
        ) {
          showInfoNotification({
            title: t('notifications.autoBetInfoTitle'),
            message: t('notifications.maxBetMessage', { maxBet }),
            autoClose: 2000,
            dataTestId: 'autobet-max-cap-reached',
          });
        }
      }
    },
    [t]
  );

  // Handle win action with percentage calculations
  const handleWinAction = useCallback(() => {
    const onWinAction = autoBetState$.onWinAction.peek();
    const onWinIncreaseValue = autoBetState$.onWinIncreaseValue.peek();
    const currentBetAmount = autoBetState$.betAmount.peek();
    const initialBetAmount = autoBetState$.initialBetAmount.peek();
    const minBet = configurationState$.minBet.peek();
    const maxBet = configurationState$.maxBet.peek();
    const decimalScale = configurationState$.decimalScale.peek();

    // Calculate what the next amount would be without caps
    let uncappedNextAmount = currentBetAmount;
    if (onWinAction === AutoBetOnWinLossAction.RESET) {
      uncappedNextAmount = initialBetAmount;
    } else if (onWinAction === AutoBetOnWinLossAction.INCREASE_OR_DECREASE) {
      uncappedNextAmount = Number(
        new Decimal(currentBetAmount)
          .mul(new Decimal(1).plus(new Decimal(onWinIncreaseValue).div(100)))
          .toFixed(2)
      );
    }

    // Use utility function to calculate next amount with caps
    const { cappedAmount, wasCapped } = calculateNextBetAmount({
      currentBetAmount,
      initialBetAmount,
      action: onWinAction,
      increaseValue: onWinIncreaseValue,
      minBet,
      maxBet,
      decimalScale,
    });

    if (wasCapped) {
      handleCapNotification(uncappedNextAmount);
    }

    autoBetState$.betAmount.set(cappedAmount);
  }, [handleCapNotification]);

  // Handle loss action with percentage calculations
  const handleLossAction = useCallback(() => {
    const onLossAction = autoBetState$.onLossAction.peek();
    const onLossIncreaseValue = autoBetState$.onLossIncreaseValue.peek();
    const currentBetAmount = autoBetState$.betAmount.peek();
    const initialBetAmount = autoBetState$.initialBetAmount.peek();
    const minBet = configurationState$.minBet.peek();
    const maxBet = configurationState$.maxBet.peek();
    const decimalScale = configurationState$.decimalScale.peek();

    // Calculate what the next amount would be without caps
    let uncappedNextAmount = currentBetAmount;
    if (onLossAction === AutoBetOnWinLossAction.RESET) {
      uncappedNextAmount = initialBetAmount;
    } else if (onLossAction === AutoBetOnWinLossAction.INCREASE_OR_DECREASE) {
      uncappedNextAmount = Number(
        new Decimal(currentBetAmount)
          .mul(new Decimal(1).plus(new Decimal(onLossIncreaseValue).div(100)))
          .toFixed(2)
      );
    }

    // Use utility function to calculate next amount with caps
    const { cappedAmount, wasCapped } = calculateNextBetAmount({
      currentBetAmount,
      initialBetAmount,
      action: onLossAction,
      increaseValue: onLossIncreaseValue,
      minBet,
      maxBet,
      decimalScale,
    });

    if (wasCapped) {
      handleCapNotification(uncappedNextAmount);
    }

    autoBetState$.betAmount.set(cappedAmount);
  }, [handleCapNotification]);

  // Check if autobetting should stop based on conditions
  const shouldStopAutoBetting = useCallback(() => {
    const conditions = {
      currentBetCount: autoBetState$.runningCurrentBetCount.peek(),
      totalProfit: autoBetState$.runningTotalProfit.peek(),
      numberOfBets: autoBetState$.numberOfBets.peek(),
      hasInfiniteBets: autoBetState$.hasInfiniteBets.peek(),
      stopOnProfitValue: autoBetState$.stopOnProfitValue.peek(),
      stopOnLossValue: autoBetState$.stopOnLossValue.peek(),
      currentBalance: walletState$.balance.peek(),
      betAmount: autoBetState$.betAmount.peek(),
    };

    const { shouldStop, reason, reasonData } = checkShouldStop(conditions);

    if (shouldStop && reason) {
      const currency = configurationState$.currency.peek();

      switch (reason) {
        case AutoBetStopReason.INSUFFICIENT_BALANCE:
          showErrorNotification({
            title: t('notifications.autobetStoppedInsufficientBalanceTitle'),
            message: t(
              'notifications.autobetStoppedInsufficientBalanceMessage'
            ),
            dataTestId: 'autobet-stopped-insufficient-balance',
          });
          break;

        case AutoBetStopReason.MAX_BETS_REACHED:
          showInfoNotification({
            title: t('notifications.autobetCompletedTitle'),
            message: t('notifications.autobetMaxBetsReachedMessage', {
              numberOfBets: reasonData?.numberOfBets,
            }),
            dataTestId: 'autobet-completed-max-bets',
          });
          break;

        case AutoBetStopReason.PROFIT_TARGET_REACHED:
          showSuccessNotification({
            title: t('notifications.autobetGoalReachedTitle'),
            message: t('notifications.autobetProfitTargetReachedMessage', {
              amount: reasonData?.stopOnProfitValue,
              currency,
            }),
            dataTestId: 'autobet-profit-target-reached',
          });
          break;

        case AutoBetStopReason.STOP_LOSS_REACHED:
          showErrorNotification({
            title: t('notifications.autobetStopLossReachedTitle'),
            message: t('notifications.autobetStopLossReachedMessage', {
              amount: reasonData?.stopOnLossValue,
              currency,
            }),
            dataTestId: 'autobet-stop-loss-reached',
          });
          break;

        default:
          // Fallback for any unexpected reason
          showInfoNotification({
            title: t('notifications.autobetStoppedTitle'),
            message: t('notifications.autobetStoppedGenericMessage'),
            dataTestId: 'autobet-stopped-max-failures',
          });
          break;
      }
    }

    return shouldStop;
  }, [t]);

  // Stop autobetting
  const stopAutoBetting = useCallback(() => {
    // Clear any pending timeouts
    if (autoBetTimeoutRef.current) {
      clearTimeout(autoBetTimeoutRef.current);
      autoBetTimeoutRef.current = null;
    }

    if (startDebounceRef.current) {
      clearTimeout(startDebounceRef.current);
      startDebounceRef.current = null;
    }

    // Reset counters
    minCapCounter.current = 0;
    maxCapCounter.current = 0;
    consecutiveFailures.current = 0;

    const totalProfit = autoBetState$.runningTotalProfit.peek();
    const betCount = autoBetState$.runningCurrentBetCount.peek();
    const currency = configurationState$.currency.peek();

    // This will handle resetting bricks and autobet state
    ratAnimationState$.setAnimationType('idle');
    autoBetState$.stopAutoBetting();

    if (betCount > 0) {
      const profitMessage = formatProfitMessage(totalProfit, currency);

      showNotification({
        title: t('notifications.autobetSummaryTitle'),
        message: t('notifications.autobetSummaryMessage', {
          count: betCount,
          profit: profitMessage,
        }),
        color: totalProfit >= 0 ? 'green' : 'red',
        dataTestId: 'autobet-summary',
      });
    }
  }, [t]);

  // Execute a single bet
  const executeNextBet = useCallback(() => {
    // Double-check we're still auto-betting
    const isAutoBetting = autoBetState$.isAutoBetting.peek();
    if (!isAutoBetting) {
      return;
    }

    const pickedBricks = autoBetState$.pickedBricks.peek();
    const currentBalance = walletState$.balance.peek();
    const coefficient = configurationState$.coeficient.peek();

    if (!token) {
      showErrorNotification({
        title: t('notifications.errorTitle'),
        message: t('notifications.noSessionTokenMessage'),
        dataTestId: 'error-no-session-token',
      });
      stopAutoBetting();
      return;
    }

    // Validate picked bricks using utility function
    const pattern = gameState$.pattern.peek();
    const { isValid } = validatePickedBricks(pickedBricks, pattern);

    if (!isValid) {
      showErrorNotification({
        title: t('notifications.errorTitle'),
        message: t('notifications.invalidBrickSelectionMessage'),
        dataTestId: 'invalid-brick-selection',
      });
      stopAutoBetting();
      return;
    }

    // Validate bet amount before sending
    const betAmount = autoBetState$.betAmount.peek();
    const { cappedAmount } = applyCaps(betAmount);

    if (cappedAmount !== betAmount) {
      autoBetState$.betAmount.set(cappedAmount);
    }

    // Check balance
    if (currentBalance < cappedAmount) {
      showErrorNotification({
        title: t('notifications.insufficientBalanceTitle'),
        message: t('notifications.insufficientBalanceMessage'),
        dataTestId: 'insufficient-balance-bet',
      });
      stopAutoBetting();
      return;
    }

    autoBetState$.setIsBetting(true);

    // Calculate actual bet amount with coefficient
    const actualBetAmount = Number(
      new Decimal(cappedAmount).mul(coefficient).toFixed(2)
    );

    const gameRoundId = sessionState$.gameRoundId.peek();

    mutation.mutate({
      body: {
        gameRoundId,
        token,
        amount: actualBetAmount,
        pattern,
        pickedBricks,
      },
    });
  }, [mutation, token, stopAutoBetting, applyCaps, t]);

  // Start autobetting with debouncing
  const startAutoBetting = useCallback(() => {
    // Clear any pending start operation
    if (startDebounceRef.current) {
      clearTimeout(startDebounceRef.current);
      startDebounceRef.current = null;
    }

    // Execute the start operation
    const executeStart = () => {
      // Check if already auto-betting
      const isAlreadyAutoBetting = autoBetState$.isAutoBetting.peek();
      if (isAlreadyAutoBetting) {
        showInfoNotification({
          title: t('notifications.infoTitle'),
          message: t('notifications.autoBettingActiveMessage'),
          dataTestId: 'info-autobet-already-active',
        });
        return;
      }

      if (!token) {
        showErrorNotification({
          title: t('notifications.errorTitle'),
          message: t('notifications.noSessionTokenMessage'),
          dataTestId: 'error-no-session-token-start',
        });
        return;
      }

      const pickedBricks = autoBetState$.pickedBricks.peek();
      const pattern = gameState$.pattern.peek();
      const { isValid } = validatePickedBricks(pickedBricks, pattern);

      if (!isValid) {
        showErrorNotification({
          title: t('notifications.errorTitle'),
          message: t('notifications.pickBricksFirstMessage'),
          dataTestId: 'error-pick-bricks-first',
        });
        return;
      }

      // Validate and cap initial bet amount
      const betAmount = autoBetState$.betAmount.peek();
      const { cappedAmount, wasCapped } = applyCaps(betAmount);
      const currentBalance = walletState$.balance.peek();

      if (wasCapped) {
        autoBetState$.betAmount.set(cappedAmount);
        handleCapNotification(betAmount);
      }

      // Check initial balance
      if (currentBalance < cappedAmount) {
        showErrorNotification({
          title: t('notifications.insufficientBalanceTitle'),
          message: t('notifications.insufficientBalanceStartMessage'),
          dataTestId: 'insufficient-balance-start',
        });
        return;
      }

      // Reset failure counter
      consecutiveFailures.current = 0;

      // Initialize autobetting
      autoBetState$.startAutoBetting();
      ratAnimationState$.setAnimationType('idle');
      playClickSound();

      showInfoNotification({
        title: t('notifications.autobetStartedTitle'),
        message: t('notifications.autobetStartedMessage'),
        dataTestId: 'autobet-started',
      });

      // Start the first bet
      executeNextBet();
    };

    // Execute immediately
    executeStart();

    // Set up debounce for subsequent calls
    startDebounceRef.current = setTimeout(() => {
      startDebounceRef.current = null;
    }, DEBOUNCE_DELAY);
  }, [
    executeNextBet,
    token,
    applyCaps,
    handleCapNotification,
    playClickSound,
    t,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoBetTimeoutRef.current) {
        clearTimeout(autoBetTimeoutRef.current);
      }
      if (startDebounceRef.current) {
        clearTimeout(startDebounceRef.current);
      }
    };
  }, []);

  const currentState = {
    isAutoBetting: autoBetState$.isAutoBetting.get(),
    isBetting: autoBetState$.isBetting.get(),
    runningCurrentBetCount: autoBetState$.runningCurrentBetCount.get(),
    runningTotalProfit: autoBetState$.runningTotalProfit.get(),
  };

  return {
    mutation,
    startAutoBetting,
    stopAutoBetting,
    ...currentState,
  };
};
